import React from 'react';
import { Spinner } from 'react-bootstrap';

const PROCESSING_STEPS = [
  { key: 'UPLOADED', label: 'Document Uploaded', description: 'File received and queued for processing' },
  { key: 'PARSING', label: 'Parsing Document', description: 'Extracting text and structure from document' },
  { key: 'ANALYZING', label: 'AI Analysis', description: 'Identifying KPIs, values, and metadata' },
  { key: 'EXTRACTING', label: 'Extracting KPIs', description: 'Mapping extracted data to known KPI definitions' },
  { key: 'COMPLETED', label: 'Ready for Review', description: 'All KPIs extracted and ready for your review' },
];

const FAILED_STEP = { key: 'FAILED', label: 'Processing Failed', description: 'An error occurred during processing' };

const getStepState = (stepIndex, currentStatus, steps) => {
  if (currentStatus === 'FAILED') {
    const statusIndex = steps.findIndex((s) => s.key === 'COMPLETED');
    if (stepIndex < statusIndex) return 'done';
    if (stepIndex === statusIndex) return 'failed';
    return 'pending';
  }

  const statusIndex = steps.findIndex((s) => s.key === currentStatus);
  if (statusIndex === -1) {
    if (currentStatus === 'PENDING') return stepIndex === 0 ? 'active' : 'pending';
    return 'pending';
  }

  if (stepIndex < statusIndex) return 'done';
  if (stepIndex === statusIndex) return currentStatus === 'COMPLETED' ? 'done' : 'active';
  return 'pending';
};

const ProcessingTracker = ({ status, errorMessage }) => {
  const isFailed = status === 'FAILED';

  return (
    <div style={{
      background: 'white',
      border: '1.5px solid #e2eaf0',
      borderRadius: 12,
      padding: '24px 28px',
      marginBottom: 28,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h6 style={{ margin: 0, fontWeight: 700, color: '#1a2e38', fontSize: '0.95rem' }}>
          Processing Status
        </h6>
        {status && (
          <StatusBadge status={status} />
        )}
      </div>

      {/* Track line + steps */}
      <div style={{ position: 'relative' }}>
        {/* Connecting line */}
        <div style={{
          position: 'absolute',
          top: 16,
          left: 16,
          right: 16,
          height: 2,
          background: '#e2eaf0',
          zIndex: 0,
        }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          {PROCESSING_STEPS.map((step, i) => {
            const state = getStepState(i, status || 'PENDING', PROCESSING_STEPS);
            return (
              <StepNode
                key={step.key}
                step={step}
                state={state}
                isLast={i === PROCESSING_STEPS.length - 1}
                isFailed={isFailed && step.key === 'COMPLETED'}
              />
            );
          })}
        </div>
      </div>

      {/* Error message */}
      {isFailed && errorMessage && (
        <div style={{
          marginTop: 16,
          background: 'rgba(231,76,60,0.08)',
          border: '1px solid rgba(231,76,60,0.2)',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: '0.85rem',
          color: '#e74c3c',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <i className="fa fa-exclamation-circle" />
          {errorMessage}
        </div>
      )}

      {/* Active step description */}
      {!isFailed && status !== 'COMPLETED' && (
        <div style={{
          marginTop: 16,
          padding: '10px 14px',
          background: '#f7fafc',
          borderRadius: 8,
          fontSize: '0.82rem',
          color: '#6b8794',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <Spinner animation="border" size="sm" style={{ color: '#3f88a5', width: 14, height: 14, borderWidth: 2 }} />
          {PROCESSING_STEPS.find(s => s.key === status)?.description || 'Document is being processed...'}
        </div>
      )}
    </div>
  );
};

const StepNode = ({ step, state, isFailed }) => {
  const colors = {
    done: { bg: '#3f88a5', border: '#3f88a5', text: 'white', labelColor: '#3f88a5' },
    active: { bg: 'white', border: '#3f88a5', text: '#3f88a5', labelColor: '#3f88a5' },
    pending: { bg: 'white', border: '#e2eaf0', text: '#b0c4ce', labelColor: '#6b8794' },
    failed: { bg: '#e74c3c', border: '#e74c3c', text: 'white', labelColor: '#e74c3c' },
  };

  const c = isFailed ? colors.failed : colors[state];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
      {/* Circle */}
      <div style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: c.bg,
        border: `2px solid ${c.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: c.text,
        fontSize: '0.75rem',
        fontWeight: 700,
        flexShrink: 0,
        transition: 'all 0.3s',
        boxShadow: state === 'active' ? `0 0 0 4px rgba(63,136,165,0.15)` : 'none',
      }}>
        {state === 'done' && !isFailed && <i className="fa fa-check" style={{ fontSize: '0.7rem' }} />}
        {isFailed && <i className="fa fa-times" style={{ fontSize: '0.7rem' }} />}
        {state === 'active' && (
          <Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2, color: '#3f88a5' }} />
        )}
        {state === 'pending' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e2eaf0' }} />}
      </div>

      {/* Label */}
      <div style={{
        marginTop: 8,
        fontSize: '0.72rem',
        fontWeight: state === 'active' ? 700 : 500,
        color: c.labelColor,
        textAlign: 'center',
        maxWidth: 90,
        lineHeight: 1.3,
      }}>
        {isFailed ? 'Failed' : step.label}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const cfg = {
    PENDING: { bg: 'rgba(230,126,34,0.12)', color: '#c87518', label: 'Pending' },
    PARSING: { bg: 'rgba(63,136,165,0.12)', color: '#3f88a5', label: 'Parsing' },
    ANALYZING: { bg: 'rgba(63,136,165,0.12)', color: '#3f88a5', label: 'Analyzing' },
    EXTRACTING: { bg: 'rgba(63,136,165,0.12)', color: '#3f88a5', label: 'Extracting' },
    COMPLETED: { bg: 'rgba(39,174,96,0.12)', color: '#27ae60', label: 'Completed' },
    FAILED: { bg: 'rgba(231,76,60,0.12)', color: '#e74c3c', label: 'Failed' },
  };
  const c = cfg[status] || { bg: 'rgba(0,0,0,0.06)', color: '#888', label: status };
  return (
    <span style={{ background: c.bg, color: c.color, fontWeight: 600, fontSize: '0.78rem', borderRadius: 6, padding: '4px 10px' }}>
      {c.label}
    </span>
  );
};

export { StatusBadge };
export default ProcessingTracker;